import { FC, useEffect } from 'react'
import { Tabs, Cascader } from 'antd'
import { CascaderOptionType } from 'antd/lib/cascader'
import { useTranslation } from 'react-i18next'
import BackgroundBox from '../../../components/custom_components/BackgroundBox'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import { PerTabBarContent } from '../../../components/custom_components/TabBarContent'
import { TalentsSeasonYearDto, TalentsSeasonStatus } from '../../../types'
import { TalentsSeasonTab } from './TalentsSeasonTab'
import { useCultivatingTalentsStore } from '../../cultivatingTalents/CultivatingTalentsStore'
import shallow from 'zustand/shallow'

const { TabPane } = Tabs

interface TalentsSeasonContentProps {
  talentsSeasonYears: TalentsSeasonYearDto[]
  isHRSettings: boolean
  isAllCompaniesSettings: boolean
  isYourCompanyView: boolean
}

export const TalentsSeasonContent: FC<TalentsSeasonContentProps> = ({
  talentsSeasonYears,
  isHRSettings,
  isAllCompaniesSettings,
  isYourCompanyView,
}) => {
  const { t } = useTranslation()
  const [setTalentsSeasonSettings, setActiveSeasonKey, activeSeasonKey, toggleRankingEditingInProgress] = useCultivatingTalentsStore(
    (state) => [state.setTalentsSeasonSettings, state.setActiveSeasonKey, state.activeSeasonKey, state.toggleRankingEditingInProgress],
    shallow,
  )

  if (!isAllCompaniesSettings && !isHRSettings && !isYourCompanyView) {
    talentsSeasonYears = talentsSeasonYears.filter((year) => {
      return year.talentsSeasonStatus !== TalentsSeasonStatus.Ready
    })
  }

  const notClosedSeasons = talentsSeasonYears.filter((year) => ![TalentsSeasonStatus.Closed].includes(year.talentsSeasonStatus))

  const closedSeasons = talentsSeasonYears.filter((year) => [TalentsSeasonStatus.Closed].includes(year.talentsSeasonStatus))

  const closedSeasonOptions: CascaderOptionType[] = closedSeasons.map((yearDto) => {
    return { label: yearDto.year.toString(), value: yearDto.year.toString() }
  })

  const previousSeasonExist = (yearDto: TalentsSeasonYearDto) => {
    const prevSeason = talentsSeasonYears.find((y) => {
      return y.year === yearDto.year - 1
    })
    return !!prevSeason
  }

  const firstClosedSeasonWithActiveKey = closedSeasons.find((year) => {
    return year.year.toString() === activeSeasonKey
  })
    ? [activeSeasonKey]
    : undefined

  const handleTabChange = (key: string) => {
    if (key !== activeSeasonKey) {
      setTalentsSeasonSettings(undefined)
      setActiveSeasonKey(key)
      toggleRankingEditingInProgress(false)
    }
  }

  const defaultSeason =
    talentsSeasonYears.find((season) => [TalentsSeasonStatus.Opened, TalentsSeasonStatus.Confirmed].includes(season.talentsSeasonStatus)) ||
    talentsSeasonYears[0]

  useEffect(() => {
    setActiveSeasonKey(defaultSeason.year.toString())
  }, [defaultSeason, setActiveSeasonKey])

  return (
    <>
      <PerTabBarContent
        color="white"
        mb="xxs"
        justify="start"
        tabs={
          <>
            <Tabs activeKey={activeSeasonKey} onChange={handleTabChange} tabBarGutter={5}>
              {notClosedSeasons.map((season) => {
                return <TabPane tab={season.year} key={season.year.toString()} />
              })}
            </Tabs>
            <BackgroundBox noPadding color="white" noShadow className="per-flexRow--growShrink per-tabbarContent--selector" mr="m">
              <PerFlexRow ml="m" align="middle" className="per-tabbarContent--height cascader-box">
                <Cascader
                  value={firstClosedSeasonWithActiveKey}
                  placeholder={t('companySettingsView.rankingTab.otherYears')}
                  options={closedSeasonOptions}
                  allowClear={false}
                  onChange={(key: string[]) => handleTabChange(key[0])}
                  disabled={!closedSeasonOptions.length}
                />
              </PerFlexRow>
            </BackgroundBox>
          </>
        }
      />

      <Tabs activeKey={activeSeasonKey} renderTabBar={() => <></>}>
        {talentsSeasonYears.map((season) => {
          return (
            <TabPane key={season.year.toString()}>
              <TalentsSeasonTab
                isHRSettings={isHRSettings}
                isAllCompaniesSettings={isAllCompaniesSettings}
                isYourCompanyView={isYourCompanyView}
                talentsSeasonYear={season}
                previousSeasonExist={previousSeasonExist(season)}
              />
            </TabPane>
          )
        })}
      </Tabs>
    </>
  )
}
